import * as React from 'react'
import { FormEvent, FunctionComponent, useContext, useEffect, useState } from 'react'
import { Dialog, DialogFooter, DialogType, Link, PrimaryButton, Stack } from '@fluentui/react'
import { getId } from '@fluentui/utilities'
import Fieldset from '../Form/Fieldset'
import { Build } from './Version'
import LabelValue from '../LabelValue/LabelValue'
import DiagnosticTextField from './DiagnosticTextField'
import useUser from '../../hooks/useUser'
import useModules from '../../hooks/module/useModules'
import { DateTime } from 'luxon'
import useSharePoint, { formatDriveId } from '../../hooks/useSharePoint'
import { useTranslation } from 'react-i18next'
import { parseDateTime } from '../../helpers/dateHelper'
import AuthorizationContext, { SecurityAuthority } from '../../context/AuthorizationContext'

type Props = {
    toggle: () => void
}

type URLSearchParamsExt = URLSearchParams & Map<string, string>

const SharePointDriveId: FunctionComponent = () => {
    const { currentEmail } = useUser()
    const { sharePoint, setDriveId, errorMessage } = useSharePoint()

    const [searchParams, setSearchParams] = useState<URLSearchParams>(new URLSearchParams())

    useEffect(() => {
        setSearchParams(
            new URLSearchParams({
                userEmail: currentEmail,
                webTitle: 'HAMMOCK',
                listTitle: 'Documenten',
                webUrl: sharePoint?.site?.webUrl,
                siteId: sharePoint?.siteId,
                webId: sharePoint?.webId,
                listId: sharePoint?.listId,
            }),
        )
    }, [currentEmail, sharePoint?.site])

    return (
        <Stack tokens={{ childrenGap: 's1' }}>
            <DiagnosticTextField
                label="Drive ID"
                value={sharePoint?.driveId}
                onChange={(_: FormEvent, value?: string) => setDriveId(value?.replace('_', '/').replace('-', '+'))}
                errorMessage={errorMessage}
            />
            {sharePoint && sharePoint.site && sharePoint.list && (
                <PrimaryButton
                    iconProps={{ iconName: 'FabricSyncFolder' }}
                    onClick={() => window.open(`odopen://sync?${searchParams.toString()}`, '_blank')}
                    text="Synchroniseren"
                />
            )}
            {sharePoint && [
                <LabelValue key={getId()} label="Drive: siteId" value={sharePoint.siteId} />,
                <LabelValue key={getId()} label="Drive: webId" value={sharePoint.webId} />,
                <LabelValue key={getId()} label="Drive: listId" value={sharePoint.listId} />,
            ]}
            {sharePoint?.site && [
                <LabelValue key={getId()} label="Site: displayName" value={sharePoint.site.displayName} />,
                <LabelValue
                    key={getId()}
                    label="Site: webUrl"
                    value={
                        <Link href={sharePoint.site.webUrl} target="_blank">
                            {sharePoint.site.webUrl}
                        </Link>
                    }
                />,
            ]}
            {sharePoint?.list && [
                <LabelValue key={getId()} label="List: displayName" value={sharePoint.list.displayName} />,
                <LabelValue
                    key={getId()}
                    label="List: webUrl"
                    value={
                        <Link href={sharePoint.list.webUrl} target="_blank">
                            {sharePoint.list.webUrl}
                        </Link>
                    }
                />,
            ]}
        </Stack>
    )
}

const SharePointLibraryId: FunctionComponent = () => {
    const [libraryId, setLibraryId] = useState<string>()
    const [labelValues, setLabelValues] = useState<{ label: string; value: string }[]>([])

    useEffect(() => {
        const params = new URLSearchParams(libraryId) as URLSearchParamsExt
        const driveId = formatDriveId({
            driveId: undefined,
            siteId: params.get('siteId'),
            webId: params.get('webId'),
            listId: params.get('listId'),
        })

        setLabelValues(
            [
                driveId && { label: 'driveId', value: driveId },
                ...[...params.entries()].map(([label, value]) => ({
                    label,
                    value: value.replaceAll(/(^{)|(}$)/g, ''),
                })),
            ].filter(item => item),
        )
    }, [libraryId])

    return (
        <Stack tokens={{ childrenGap: 's1' }}>
            <DiagnosticTextField
                label="Synchronisatie URL (Library ID)"
                value={libraryId}
                onChange={(_: FormEvent, value?: string) => setLibraryId(value)}
            />
            {labelValues.map(labelValue => (
                <LabelValue key={getId()} label={labelValue.label} value={labelValue.value} />
            ))}
        </Stack>
    )
}

const SharePointFieldset: FunctionComponent = () => {
    const { tokens } = useContext(AuthorizationContext)

    return (
        <Fieldset legend="SharePoint" canToggle>
            <Stack tokens={{ childrenGap: 'm' }}>
                <SharePointDriveId />
                <DiagnosticTextField label="Token" multiline rows={5} readOnly value={tokens?.[SecurityAuthority.SharePoint]?.access_token} />
                <SharePointLibraryId />
            </Stack>
        </Fieldset>
    )
}

const HammockFieldset: FunctionComponent = () => {
    const { tokens, roles } = useContext(AuthorizationContext)
    const { t: translate } = useTranslation()
    const { modules } = useModules()

    return (
        <Fieldset legend="Hammock" canToggle>
            <Stack tokens={{ childrenGap: 'm' }}>
                <DiagnosticTextField label="Token" multiline rows={5} readOnly value={tokens?.[SecurityAuthority.Hammock]?.access_token} />
                <LabelValue key={getId()} label={translate('support.toolbox.diagnostics.roles')} value={roles?.join(', ')} />
                <LabelValue
                    key={getId()}
                    label={translate('support.toolbox.diagnostics.modules')}
                    value={modules?.map(module => translate(`file.module.${module.type?.toLowerCase()}`) ?? 'unknown').join(', ')}
                />
            </Stack>
        </Fieldset>
    )
}

const WebPubSubFieldset: FunctionComponent = () => {
    const { tokens } = useContext(AuthorizationContext)
    const { t: translate } = useTranslation()

    return (
        <Fieldset legend="Web PubSub" canToggle>
            <Stack tokens={{ childrenGap: 'm' }}>
                <DiagnosticTextField label="Token" multiline rows={5} readOnly value={tokens?.[SecurityAuthority.WebPubSub]?.access_token} />
                <LabelValue
                    key={getId()}
                    label={translate('support.toolbox.diagnostics.expirydate')}
                    value={parseDateTime(DateTime.fromISO(tokens?.[SecurityAuthority.WebPubSub]?.expires_at)).toFormat('dd-LL-yyyy HH:mm:ss')}
                />
            </Stack>
        </Fieldset>
    )
}

const DiagnosticsDialog: FunctionComponent<Props> = props => {
    const { t: translate } = useTranslation()
    const { authenticated, mailbox, user, host: currentHost } = useContext(AuthorizationContext)

    const lastCommitDateTime = DateTime.fromISO(process.env.LASTCOMMITDATETIME).toFormat('dd-LL-yyyy HH:mm:ss')
    const gitString = `Git ${process.env.COMMITHASH.substring(0, 8)} @ ${lastCommitDateTime}`

    return (
        <Dialog
            hidden={false}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: translate('support.toolbox.diagnostics.title'),
            }}
            minWidth={480}
            maxWidth={720}
        >
            <DiagnosticTextField
                multiline
                autoAdjustHeight
                readOnly
                resizable={false}
                value={`Env: ${currentHost?.Name || 'Geen omgeving'}
Version: v${(process.env.BUILD as unknown as Build).version}
Mailbox: ${mailbox || 'Geen mailbox'}
User: ${user?.username || 'Geen gebruiker'}
Git: ${gitString}
Url: ${window.location.href}
Agent: ${window.navigator.userAgent}`}
            />

            {authenticated && <SharePointFieldset />}
            {authenticated && <HammockFieldset />}
            {authenticated && <WebPubSubFieldset />}
            <DialogFooter>
                <PrimaryButton onClick={props.toggle} text="Sluiten" />
            </DialogFooter>
        </Dialog>
    )
}

export default DiagnosticsDialog
